import { useMemo, useState, type ReactNode } from "react";

import FilterDropdown from "./FilterDropdown";

export type BelegReviewTimelineEntry = {
  id: string;
  createdAt: string | null;
  action: string;
  status: string;
  actorId: string | null;
  actorLabel: string | null;
  comment: string | null;
  position?: string | null;
  note?: string | null;
  details?: ReactNode;
};

function eventMonth(value: string | null): string {
  if (!value) return "Ohne Datum";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Ohne Datum";
  return new Intl.DateTimeFormat("de-AT", { month: "long", year: "numeric" }).format(date);
}

function eventTime(value: string | null): string {
  if (!value) return "Zeitpunkt unbekannt";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-AT", { dateStyle: "short", timeStyle: "short" }).format(date);
}

export default function BelegReviewTimeline({
  entries,
  emptyText = "Noch keine Entscheidung gespeichert.",
}: {
  entries: BelegReviewTimelineEntry[];
  emptyText?: string;
}) {
  const [status, setStatus] = useState("all");
  const [actor, setActor] = useState("all");
  const [period, setPeriod] = useState("all");
  const [group, setGroup] = useState("date");

  const statusOptions = useMemo(() => [
    { value: "all", label: "Alle Status" },
    ...Array.from(new Set(entries.map((entry) => entry.status))).sort().map((value) => ({ value, label: value })),
  ], [entries]);
  const actorOptions = useMemo(() => {
    const actors = new Map<string, string>();
    for (const entry of entries) actors.set(entry.actorId ?? "unknown", entry.actorLabel ?? entry.actorId ?? "Unbekannt");
    return [
      { value: "all", label: "Alle Personen" },
      ...Array.from(actors, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "de")),
    ];
  }, [entries]);
  const visible = useMemo(() => {
    const now = Date.now();
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
    const cutoff = period === "30d" ? now - 30 * 86_400_000 : period === "12m" ? twelveMonthsAgo.getTime() : null;
    return entries.filter((entry) =>
      (status === "all" || entry.status === status) &&
      (actor === "all" || (entry.actorId ?? "unknown") === actor) &&
      (cutoff === null || (entry.createdAt !== null && Date.parse(entry.createdAt) >= cutoff)),
    ).sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "") || b.id.localeCompare(a.id));
  }, [entries, status, actor, period]);
  const groups = useMemo(() => {
    if (group === "date") return [["", visible]] as const;
    const byMonth = new Map<string, BelegReviewTimelineEntry[]>();
    for (const entry of visible) {
      const month = eventMonth(entry.createdAt);
      byMonth.set(month, [...(byMonth.get(month) ?? []), entry]);
    }
    return Array.from(byMonth);
  }, [group, visible]);

  return (
    <section className="mt-8 border-t border-slate-200 pt-5" aria-labelledby="beleg-pruefverlauf-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="beleg-pruefverlauf-heading" className="text-base font-semibold text-slate-900">Prüfverlauf</h2>
        <span className="text-xs text-slate-500">{visible.length} von {entries.length} Einträgen</span>
      </div>
      {entries.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <FilterDropdown label="Status" ariaLabel="Prüfverlauf nach Status filtern" options={statusOptions} value={status} defaultValue="all" onChange={setStatus} showValue />
          <FilterDropdown label="Person" ariaLabel="Prüfverlauf nach Person filtern" options={actorOptions} value={actor} defaultValue="all" onChange={setActor} showValue />
          <FilterDropdown label="Zeitraum" ariaLabel="Prüfverlauf nach Zeitraum filtern" options={[{ value: "all", label: "Gesamter Zeitraum" }, { value: "30d", label: "Letzte 30 Tage" }, { value: "12m", label: "Letzte 12 Monate" }]} value={period} defaultValue="all" onChange={setPeriod} showValue />
          <FilterDropdown label="Gruppierung" ariaLabel="Prüfverlauf gruppieren" options={[{ value: "date", label: "Chronologisch" }, { value: "month", label: "Nach Monat" }]} value={group} defaultValue="date" onChange={setGroup} showValue />
        </div>
      ) : null}
      {visible.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">{entries.length === 0 ? emptyText : "Keine Einträge für diese Auswahl."}</p>
      ) : groups.map(([heading, groupEntries]) => (
        <div key={heading || "all"} className="mt-4">
          {heading ? <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{heading}</h3> : null}
          <ol className="divide-y divide-slate-200 border-t border-slate-200">
            {groupEntries.map((entry) => (
              <li key={entry.id} className="py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-sm font-medium text-slate-900">{entry.action}</span>
                  {entry.position ? <span className="text-xs text-slate-600">Position {entry.position}</span> : null}
                  <span className="text-xs text-slate-500">{eventTime(entry.createdAt)} · {entry.actorLabel ?? entry.actorId ?? "Unbekannt"}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">Status: {entry.status}{entry.note ? ` · ${entry.note}` : ""}</p>
                {entry.comment ? <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{entry.comment}</p> : null}
                {entry.details}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}
